import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import RoutePreviewMap from '../components/RoutePreviewMap';
import DssReasons from '../components/DssReasons';
import {
  DASHBOARD_POLL_MS,
  ROADS_UPDATED_KEY,
  conflictMessage,
  formatKm,
  formatPct,
  todayISO,
  vehicleLabel,
} from '../lib/logisticsUi';
import './DashboardPages.css';

function gpsClass(status) {
  if (status === 'fresh') return 'gps-chip gps-fresh';
  if (status === 'stale') return 'gps-chip gps-stale';
  return 'gps-chip gps-offline';
}

function DispatchBoardPage() {
  const [date, setDate] = useState(todayISO);
  const [clusters, setClusters] = useState([]);
  const [fleetById, setFleetById] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roadsBanner, setRoadsBanner] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [selectedClusterId, setSelectedClusterId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [frozenId, setFrozenId] = useState(null);

  const [customMode, setCustomMode] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const [assignModal, setAssignModal] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const loadBoard = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const [{ clusters: rows }, live] = await Promise.all([
        api.getClusters(date || undefined, 'open'),
        api.getFleetLive().catch(() => ({ drivers: [] })),
      ]);
      setClusters(rows || []);
      const map = {};
      (live.drivers || []).forEach((d) => {
        map[d.driver_id] = d;
      });
      setFleetById(map);
      setLastUpdated(new Date());
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(ROADS_UPDATED_KEY)) {
        setRoadsBanner(true);
      }
    } catch (err) {
      setError(err.message);
      if (!silent) setClusters([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [date]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    const id = setInterval(() => loadBoard({ silent: true }), DASHBOARD_POLL_MS);
    return () => clearInterval(id);
  }, [loadBoard]);

  useEffect(() => {
    if (!customMode) return undefined;
    let cancelled = false;
    api.getOrders('pending').then(({ orders }) => {
      if (!cancelled) setPendingOrders(orders || []);
    }).catch(() => {
      if (!cancelled) setPendingOrders([]);
    });
    return () => { cancelled = true; };
  }, [customMode]);

  const selectedCluster = useMemo(
    () => clusters.find((c) => Number(c.cluster_id) === Number(selectedClusterId)) || null,
    [clusters, selectedClusterId]
  );

  const runPreview = async (payload) => {
    try {
      setPreviewLoading(true);
      setError(null);
      setConflict(null);
      const data = await api.previewRoute(payload);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSelectCluster = (cluster) => {
    if (frozenId && Number(frozenId) === Number(cluster.cluster_id)) return;
    setCustomMode(false);
    setSelectedClusterId(cluster.cluster_id);
    setPreview(null);
    runPreview({ cluster_id: cluster.cluster_id });
  };

  const handleRecompute = async () => {
    try {
      setRecomputing(true);
      setError(null);
      setConflict(null);
      await api.recomputeClusters();
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(ROADS_UPDATED_KEY);
      setRoadsBanner(false);
      setSelectedClusterId(null);
      setPreview(null);
      await loadBoard();
    } catch (err) {
      setError(err.message);
    } finally {
      setRecomputing(false);
    }
  };

  const handleCustomPreview = () => {
    if (selectedOrderIds.length === 0) {
      setError('Select at least one pending order.');
      return;
    }
    setSelectedClusterId(null);
    runPreview({ order_ids: selectedOrderIds });
  };

  const toggleOrder = (orderId) => {
    setSelectedOrderIds((prev) => (
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    ));
  };

  const openAssign = (rec) => {
    const orderIds = rec.route?.order_ids
      || preview?.candidate?.orders?.map((o) => o.order_id)
      || [];
    setAssignModal({
      driver: rec.driver,
      route: rec.route,
      cluster_id: preview?.candidate?.cluster_id || selectedClusterId,
      order_ids: orderIds,
      savings_km: rec.route?.savings_km,
      route_id: preview?.route_id || rec.route?.route_id,
    });
  };

  const confirmAssign = async () => {
    if (!assignModal) return;
    const freezeKey = assignModal.cluster_id || `custom-${assignModal.order_ids.join('-')}`;
    try {
      setAssigning(true);
      setFrozenId(freezeKey);
      setError(null);
      setConflict(null);
      if (assignModal.route_id) {
        await api.acceptRoute(assignModal.route_id, assignModal.driver.driver_id);
      } else {
        const body = { driver_id: assignModal.driver.driver_id };
        if (assignModal.cluster_id) body.cluster_id = assignModal.cluster_id;
        else body.order_ids = assignModal.order_ids;
        await api.createRoute(body);
      }
      setSuccess(`Assigned ${assignModal.driver.name} to ${assignModal.order_ids.length} order(s).`);
      setAssignModal(null);
      setPreview(null);
      setSelectedClusterId(null);
      setSelectedOrderIds([]);
      await loadBoard();
    } catch (err) {
      if (err.status === 409) {
        setConflict(conflictMessage(err));
        setAssignModal(null);
      } else {
        setError(err.message);
      }
    } finally {
      setAssigning(false);
      setFrozenId(null);
    }
  };

  const bestRoute = preview?.recommendations?.[0]?.route || null;
  const recommendations = preview?.recommendations || [];

  return (
    <div className="dashboard-page">
      <h1>Dispatch Board</h1>
      <p className="dashboard-subtitle">
        Review clustered orders, preview a multi-stop route, and assign the ranked driver.
      </p>

      <div className="orders-toolbar">
        <label className="dispatch-date-label" htmlFor="dispatch-date">Pickup date</label>
        <input
          id="dispatch-date"
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setSelectedClusterId(null);
            setPreview(null);
          }}
        />
        <button type="button" className="orders-refresh-btn" onClick={() => loadBoard()} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        <button type="button" className="entity-add-btn" onClick={handleRecompute} disabled={recomputing}>
          {recomputing ? 'Recomputing…' : 'Recompute clusters'}
        </button>
        {lastUpdated && (
          <span className="orders-last-updated">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {roadsBanner && (
        <div className="dispatch-banner dispatch-banner-info">
          Graph updated — recompute clusters to apply the latest road conditions.
        </div>
      )}
      {success && <div className="orders-success">{success}</div>}
      {conflict && <div className="orders-error">{conflict}</div>}
      {error && <div className="orders-error">{error}</div>}

      <label className="dispatch-custom-toggle">
        <input
          type="checkbox"
          checked={customMode}
          onChange={(e) => {
            setCustomMode(e.target.checked);
            if (e.target.checked) {
              setSelectedClusterId(null);
              setPreview(null);
            }
          }}
        />
        Build custom route
      </label>

      {customMode && (
        <section className="entity-manual-panel">
          <h2 className="entity-manual-title">Select pending orders</h2>
          <p className="orders-hint entity-manual-hint">
            Use this when nearby orders did not land in the same cluster.
          </p>
          {pendingOrders.length === 0 ? (
            <p className="orders-hint">No pending orders.</p>
          ) : (
            <ul className="custom-order-list">
              {pendingOrders.map((o) => (
                <li key={o.order_id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.includes(o.order_id)}
                      onChange={() => toggleOrder(o.order_id)}
                    />
                    #{o.order_id} {o.pickup_location} → {o.delivery_location}
                    {o.produce_type ? ` · ${o.produce_type}` : ''}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="entity-add-btn" onClick={handleCustomPreview} disabled={previewLoading}>
            Preview custom route
          </button>
        </section>
      )}

      {loading ? (
        <div className="orders-placeholder"><p>Loading dispatch board…</p></div>
      ) : (
        <div className="dispatch-board">
          <section className="dispatch-col">
            <h2 className="entity-list-heading">Clusters</h2>
            {clusters.length === 0 ? (
              <div className="orders-placeholder">
                <p>No pending clusters. New WhatsApp orders will appear here after recompute.</p>
                <button type="button" className="entity-add-btn" onClick={handleRecompute} disabled={recomputing}>
                  {recomputing ? 'Recomputing…' : 'Recompute clusters'}
                </button>
              </div>
            ) : (
              <div className="cluster-card-list">
                {clusters.map((c) => {
                  const selected = Number(c.cluster_id) === Number(selectedClusterId);
                  const frozen = frozenId && Number(frozenId) === Number(c.cluster_id);
                  return (
                    <button
                      type="button"
                      key={c.cluster_id}
                      className={`cluster-card ${selected ? 'is-selected' : ''} ${frozen ? 'is-frozen' : ''}`}
                      onClick={() => handleSelectCluster(c)}
                      disabled={Boolean(frozen)}
                    >
                      <div className="cluster-card-title">
                        {c.order_count} order{c.order_count === 1 ? '' : 's'} → {c.dest_key || '—'}
                      </div>
                      <div className="cluster-card-meta">
                        {c.zone || c.region || 'Zone —'}
                        {c.cold_chain ? <span className="cold-badge">Cold chain</span> : null}
                        {' · '}{Math.round(Number(c.total_weight_kg) || 0)} kg
                      </div>
                      <div className="score-bar" aria-hidden="true">
                        <span style={{ width: `${Math.min(100, Math.round((Number(c.score) || 0) * 100))}%` }} />
                      </div>
                      <div className="cluster-chips">
                        {(c.orders || []).map((o) => (
                          <span key={o.order_id} className="cluster-chip">
                            {o.pickup_location} · {o.produce_type || 'produce'}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="dispatch-col">
            <h2 className="entity-list-heading">Route preview</h2>
            {previewLoading ? (
              <div className="orders-placeholder route-skeleton">
                <p>Building stop sequence…</p>
              </div>
            ) : (
              <RoutePreviewMap route={bestRoute} />
            )}
            {preview?.candidate && (
              <p className="orders-hint">
                {preview.candidate.order_count} order(s) · {Math.round(preview.candidate.total_weight_kg || 0)} kg
                {preview.candidate.cold_chain ? ' · cold chain' : ''}
                {selectedCluster ? ` · cluster #${selectedCluster.cluster_id}` : ''}
              </p>
            )}
          </section>

          <section className="dispatch-col">
            <h2 className="entity-list-heading">Recommended drivers</h2>
            {!preview && !previewLoading && (
              <div className="orders-placeholder">
                <p>DSS ranking appears after you preview a cluster or custom route.</p>
              </div>
            )}
            {preview && recommendations.length === 0 && (
              <div className="orders-placeholder">
                <p>No eligible driver for this load. Check vehicle capacity, cold chain, or active trips.</p>
              </div>
            )}
            <div className="dss-list">
              {recommendations.map((rec) => {
                const live = fleetById[rec.driver.driver_id];
                const gps = live?.gps || 'offline';
                const fill = rec.route?.utilization || 0;
                return (
                  <article key={rec.driver.driver_id} className="dss-card">
                    <div className="dss-card-head">
                      <strong>{rec.driver.name}</strong>
                      <span className={gpsClass(gps)}>{gps}</span>
                    </div>
                    <p className="dss-card-meta">
                      {vehicleLabel(rec.driver.vehicle_type)} · score {rec.dss_score}
                      {live?.trip_id ? ` · trip #${live.trip_id}` : ''}
                    </p>
                    <div className="score-bar score-bar-util" aria-hidden="true">
                      <span style={{ width: `${Math.min(100, Math.round((Number(fill) || 0) * 100))}%` }} />
                    </div>
                    <p className="orders-hint">{formatPct(fill)} utilization · {formatKm(rec.route?.savings_km)} saved</p>
                    <DssReasons reasons={rec.reasons} />
                    <button type="button" className="order-assign-btn" onClick={() => openAssign(rec)}>
                      Assign {rec.driver.name}
                    </button>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={() => !assigning && setAssignModal(null)}>
          <div className="modal-content modal-form modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm assignment</h3>
            <p>
              Assign <strong>{assignModal.driver.name}</strong> to{' '}
              {assignModal.order_ids.map((id) => `#${id}`).join(', ')}.
            </p>
            <p className="orders-hint">
              Estimated savings {formatKm(assignModal.savings_km)}. The driver will receive the sequenced
              stop list on WhatsApp.
            </p>
            <div className="modal-actions">
              <button type="button" className="modal-close-btn" onClick={() => setAssignModal(null)} disabled={assigning}>
                Cancel
              </button>
              <button type="button" className="order-assign-btn" onClick={confirmAssign} disabled={assigning}>
                {assigning ? 'Assigning…' : 'Confirm assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DispatchBoardPage;
